import { useEffect, useState } from "react";
import { FileText, KeyRound, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, openPdf } from "../api";
import DataTable from "../components/DataTable";
import InspectionDialog from "../components/InspectionDialog";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { fmtDateTime, fmtMoney, fmtNum, toDatetimeLocal } from "../utils/format";

export default function Rentals() {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [retFor, setRetFor] = useState(null);
  const [newCustomer, setNewCustomer] = useState(false);
  const [form, setForm] = useState({
    customer_id: "", new_customer: { name: "", phone: "" }, vehicle_id: "",
    start_datetime: toDatetimeLocal(), expected_return: toDatetimeLocal(new Date(Date.now() + 86400000)),
    daily_price: "", weekly_price: "", monthly_price: "", reduction: "", deposit: "", km_start: "",
    fuel_start: "", driver: "", conditions: "", account_id: "",
  });
  const [retForm, setRetForm] = useState({ actual_return: "", km_end: "", fuel_end: "", amount_paid: "", account_id: "" });
  const [inspFor, setInspFor] = useState(null);
  const [prolongFor, setProlongFor] = useState(null);
  const [prolongDate, setProlongDate] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const delRental = async (r) => {
    if (!window.confirm(`Supprimer la location ${r.rental_number} ?${["En cours", "Réservée", "Prolongée"].includes(r.status) ? " Le véhicule redeviendra disponible." : ""}`)) return;
    try {
      await api.delete(`/rentals/${r.id}`);
      toast.success("Location supprimée");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const load = () => api.get("/rentals").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const rentable = vehicles.filter((v) => ["Disponible", "Dédouané", "En entretien"].includes(v.status));

  const submit = async () => {
    if (!form.vehicle_id) return toast.error("Véhicule requis");
    if (!newCustomer && !form.customer_id) return toast.error("Client requis");
    try {
      await api.post("/rentals", {
        ...form,
        daily_price: Number(form.daily_price) || 0, weekly_price: Number(form.weekly_price) || 0,
        monthly_price: Number(form.monthly_price) || 0, deposit: Number(form.deposit) || 0,
        reduction: Number(form.reduction) || 0,
        km_start: form.km_start ? Number(form.km_start) : null,
        account_id: form.account_id || null,
        customer_id: newCustomer ? null : form.customer_id,
        new_customer: newCustomer ? form.new_customer : null,
      });
      toast.success("Location enregistrée");
      setOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const submitReturn = async () => {
    try {
      const r = await api.post(`/rentals/${retFor.id}/return`, {
        ...retForm,
        amount_paid: Number(retForm.amount_paid) || 0,
        km_end: retForm.km_end ? Number(retForm.km_end) : null,
        account_id: retForm.account_id || null,
        actual_return: retForm.actual_return || null,
      });
      toast.success(`Location clôturée — ${r.data.days} jour(s) · ${fmtMoney(r.data.total_amount)}`);
      setRetFor(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="rentals-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Locations</h1>
          <p className="mt-1 text-sm text-slate-500">Le système empêche toute double réservation sur la même période.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-rental-button"><Plus className="mr-1 h-4 w-4" /> Nouvelle location</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nouvelle location</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 flex items-center justify-between">
                <Label>Client *</Label>
                <button type="button" className="text-xs font-medium text-blue-600 hover:underline" onClick={() => setNewCustomer(!newCustomer)} data-testid="rental-toggle-new-customer">
                  {newCustomer ? "Client existant" : "+ Nouveau client"}
                </button>
              </div>
              {newCustomer ? (
                <>
                  <div><Label>Nom</Label><Input value={form.new_customer.name} onChange={(e) => set("new_customer", { ...form.new_customer, name: e.target.value })} data-testid="rental-new-customer-name" /></div>
                  <div><Label>Téléphone</Label><Input value={form.new_customer.phone} onChange={(e) => set("new_customer", { ...form.new_customer, phone: e.target.value })} /></div>
                </>
              ) : (
                <div className="col-span-2">
                  <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
                    <SelectTrigger data-testid="rental-customer-select"><SelectValue placeholder="Choisir un client…" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.phone}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2"><Label>Véhicule * (disponibles uniquement)</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => set("vehicle_id", v)}>
                  <SelectTrigger data-testid="rental-vehicle-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{rentable.map((v) => <SelectItem key={v.id} value={v.id}>{v.internal_number} — {v.brand} {v.model}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Départ</Label><Input type="datetime-local" value={form.start_datetime} onChange={(e) => set("start_datetime", e.target.value)} data-testid="rental-start" /></div>
              <div><Label>Retour prévu</Label><Input type="datetime-local" value={form.expected_return} onChange={(e) => set("expected_return", e.target.value)} data-testid="rental-expected-return" /></div>
              <div><Label>Prix journalier</Label><Input type="number" value={form.daily_price} onChange={(e) => set("daily_price", e.target.value)} data-testid="rental-daily-price" /></div>
              <div><Label>Prix hebdomadaire</Label><Input type="number" value={form.weekly_price} onChange={(e) => set("weekly_price", e.target.value)} /></div>
              <div><Label>Prix mensuel</Label><Input type="number" value={form.monthly_price} onChange={(e) => set("monthly_price", e.target.value)} /></div>
              <div><Label>Réduction (FCFA)</Label><Input type="number" value={form.reduction} onChange={(e) => set("reduction", e.target.value)} data-testid="rental-reduction" /></div>
              <div><Label>Caution (FCFA)</Label><Input type="number" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} data-testid="rental-deposit" /></div>
              <div><Label>Km départ</Label><Input type="number" value={form.km_start} onChange={(e) => set("km_start", e.target.value)} /></div>
              <div><Label>Carburant départ</Label><Input value={form.fuel_start} onChange={(e) => set("fuel_start", e.target.value)} placeholder="ex: 3/4" /></div>
              <div><Label>Chauffeur (optionnel)</Label><Input value={form.driver} onChange={(e) => set("driver", e.target.value)} /></div>
              <div><Label>Compte (caution)</Label>
                <Select value={form.account_id} onValueChange={(v) => set("account_id", v === "none" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Conditions particulières</Label><Textarea rows={2} value={form.conditions} onChange={(e) => set("conditions", e.target.value)} /></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="rental-submit-button">Enregistrer la location</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable testidPrefix="rentals-table" rows={rows}
        searchKeys={["rental_number", "customer_name", "vehicle_label"]}
        columns={[
          { key: "rental_number", label: "N°", render: (r) => <span className="font-mono-num font-semibold">{r.rental_number}</span> },
          { key: "customer_name", label: "Client" },
          { key: "vehicle_label", label: "Véhicule" },
          { key: "start_datetime", label: "Départ", render: (r) => fmtDateTime(r.start_datetime) },
          { key: "expected_return", label: "Retour prévu", render: (r) => fmtDateTime(r.expected_return) },
          { key: "deposit", label: "Caution", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.deposit)}</span> },
          { key: "total_amount", label: "Montant", className: "text-right", render: (r) => <span className="font-mono-num font-semibold">{r.total_amount ? fmtMoney(r.total_amount) : "—"}</span> },
          { key: "status", label: "Statut", render: (r) => <StatusBadge status={r.status} /> },
          { key: "actions", label: "", sortable: false, render: (r) => (
            <div className="flex flex-wrap gap-1">
              {!["Terminée", "Annulée"].includes(r.status) && (
                <>
                  <Button size="sm" variant="outline" data-testid={`inspection-depart-${r.rental_number}`}
                    onClick={() => setInspFor({ rental: r, type: "depart" })}>État départ</Button>
                  <Button size="sm" variant="outline" data-testid={`inspection-retour-${r.rental_number}`}
                    onClick={() => setInspFor({ rental: r, type: "retour" })}>État retour</Button>
                  <Button size="sm" variant="outline" data-testid={`prolong-rental-${r.rental_number}`}
                    onClick={() => { setProlongFor(r); setProlongDate(r.expected_return?.slice(0, 16) || ""); }}>Prolonger</Button>
                  <Button size="sm" variant="outline" data-testid={`return-rental-${r.rental_number}`}
                    onClick={() => { setRetFor(r); setRetForm({ actual_return: toDatetimeLocal(), km_end: "", fuel_end: "", amount_paid: "", account_id: "" }); }}>
                    <KeyRound className="mr-1 h-3.5 w-3.5" /> Retour
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-500" data-testid={`cancel-rental-${r.rental_number}`}
                    onClick={async () => { if (window.confirm(`Annuler ${r.rental_number} ?`)) { await api.post(`/rentals/${r.id}/cancel`); toast.success("Location annulée"); load(); } }}>
                    <X className="h-4 w-4" />
                  </Button>
                </>
              )}
              <Button size="sm" variant="ghost" title="Contrat PDF" onClick={() => openPdf(`/rentals/${r.id}/contract.pdf`)} data-testid={`contract-pdf-${r.rental_number}`}><FileText className="h-4 w-4" /></Button>
              {isAdmin && <Button size="sm" variant="ghost" className="text-red-500" title="Supprimer la location" onClick={() => delRental(r)} data-testid={`delete-rental-${r.rental_number}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ) },
        ]}
      />

      <Dialog open={!!prolongFor} onOpenChange={() => setProlongFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Prolonger — {prolongFor?.rental_number}</DialogTitle></DialogHeader>
          <div><Label>Nouvelle date de retour prévue</Label>
            <Input type="datetime-local" value={prolongDate} onChange={(e) => setProlongDate(e.target.value)} data-testid="prolong-date" />
          </div>
          <DialogFooter>
            <Button data-testid="prolong-submit" onClick={async () => {
              try {
                await api.post(`/rentals/${prolongFor.id}/prolong`, { new_expected_return: prolongDate });
                toast.success("Location prolongée");
                setProlongFor(null);
                load();
              } catch (e) { toast.error(fmtErr(e)); }
            }}>Confirmer la prolongation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {inspFor && (
        <InspectionDialog rental={inspFor.rental} type={inspFor.type} open={!!inspFor}
          onClose={() => setInspFor(null)} onSaved={load} />
      )}

      <Dialog open={!!prolongFor} onOpenChange={() => setProlongFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Prolonger — {prolongFor?.rental_number}</DialogTitle></DialogHeader>
          <div><Label>Nouvelle date de retour prévue</Label>
            <Input type="datetime-local" value={prolongDate} onChange={(e) => setProlongDate(e.target.value)} data-testid="prolong-date" />
          </div>
          <DialogFooter>
            <Button data-testid="prolong-submit" onClick={async () => {
              try {
                await api.post(`/rentals/${prolongFor.id}/prolong`, { new_expected_return: prolongDate });
                toast.success("Location prolongée");
                setProlongFor(null);
                load();
              } catch (e) { toast.error(fmtErr(e)); }
            }}>Confirmer la prolongation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {inspFor && (
        <InspectionDialog rental={inspFor.rental} type={inspFor.type} open={!!inspFor}
          onClose={() => setInspFor(null)} onSaved={load} />
      )}

      <Dialog open={!!retFor} onOpenChange={() => setRetFor(null)}>        <DialogContent>
          <DialogHeader><DialogTitle>Retour véhicule — {retFor?.rental_number}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Retour réel</Label><Input type="datetime-local" value={retForm.actual_return} onChange={(e) => setRetForm({ ...retForm, actual_return: e.target.value })} data-testid="return-actual-date" /></div>
            <div><Label>Km retour</Label><Input type="number" value={retForm.km_end} onChange={(e) => setRetForm({ ...retForm, km_end: e.target.value })} data-testid="return-km" /></div>
            <div><Label>Carburant retour</Label><Input value={retForm.fuel_end} onChange={(e) => setRetForm({ ...retForm, fuel_end: e.target.value })} /></div>
            <div><Label>Montant payé (FCFA)</Label><Input type="number" value={retForm.amount_paid} onChange={(e) => setRetForm({ ...retForm, amount_paid: e.target.value })} data-testid="return-amount" /></div>
            <div className="col-span-2"><Label>Compte crédité</Label>
              <Select value={retForm.account_id} onValueChange={(v) => setRetForm({ ...retForm, account_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="return-account"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={submitReturn} data-testid="return-submit">Clôturer la location</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
